<template>
    <div ref="root" class="ic-carousel" :style="cssVars">
        <div
            ref="viewport"
            class="ic-viewport"
            :class="{ 'ic-dragging': dragging }"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerUp"
        >
            <div
                ref="track"
                class="ic-track"
                :style="trackStyle"
                @transitionend="onTransitionEnd"
            >
                <!-- El set se repite las veces necesarias para que el bucle infinito no tenga huecos -->
                <div
                    v-for="(image, index) in loopImages"
                    :key="index"
                    class="ic-item"
                >
                    <img
                        :src="image"
                        class="ic-img"
                        loading="lazy"
                        decoding="async"
                        draggable="false"
                        alt=""
                    />
                </div>
            </div>
        </div>

        <button
            v-if="showArrows"
            type="button"
            class="ic-arrow ic-arrow--prev"
            aria-label="Anterior"
            @click="prev"
        >
            <span class="ic-arrow__icon">&#8249;</span>
        </button>
        <button
            v-if="showArrows"
            type="button"
            class="ic-arrow ic-arrow--next"
            aria-label="Siguiente"
            @click="next"
        >
            <span class="ic-arrow__icon">&#8250;</span>
        </button>
    </div>
</template>

<script>
    export default {
        name: 'PhotoCarousel',

        props: {
            // Listado de URLs de las imágenes
            images: {
                type: Array,
                required: true,
            },
            // Relación de aspecto de cada foto (cualquier valor válido de CSS aspect-ratio)
            aspectRatio: {
                type: String,
                default: '4 / 3',
            },
            // Nº de fotos visibles en escritorio (admite decimales: 4.5 deja entrever la siguiente)
            itemsDesktop: {
                type: Number,
                default: 4.5,
            },
            // Nº de fotos visibles en móvil
            itemsMobile: {
                type: Number,
                default: 1.5,
            },
            // Separación entre fotos en píxeles
            gap: {
                type: Number,
                default: 16,
            },
            // Nº de fotos que avanza cada pulsación de flecha
            step: {
                type: Number,
                default: 1,
            },
            // Mostrar las flechas de navegación
            showArrows: {
                type: Boolean,
                default: true,
            },
            // Encajar (snap) a la foto más cercana al soltar el arrastre / pulsar flecha
            snap: {
                type: Boolean,
                default: true,
            },
            // Duración de la animación de las flechas/snap en milisegundos
            transitionMs: {
                type: Number,
                default: 400,
            },
            // Radio de borde de cada foto
            borderRadius: {
                type: String,
                default: '15px',
            },
            // Punto de ruptura escritorio/móvil en píxeles
            breakpoint: {
                type: Number,
                default: 768,
            },
        },

        data() {
            return {
                itemWidth: 0,
                repeats: 2,
                // Desplazamiento que centra la banda de fotos visibles dejando ver una
                // porción de la anterior y la siguiente (móvil y escritorio)
                centerOffset: 0,
                position: 0,          // desplazamiento lógico en px (0 = inicio)
                dragging: false,
                useTransition: false,
                lastPointerX: 0,
            };
        },

        computed: {
            // Repetimos el set para cubrir el ancho visible más un set extra (bucle sin saltos)
            loopImages() {
                const out = [];
                for (let i = 0; i < this.repeats; i++) {
                    out.push(...this.images);
                }
                return out;
            },

            itemStep() {
                return this.itemWidth + this.gap;
            },

            oneSetWidth() {
                return this.images.length * this.itemStep;
            },

            cssVars() {
                return {
                    '--ic-gap': this.gap + 'px',
                    '--ic-item-width': this.itemWidth + 'px',
                    '--ic-aspect': this.aspectRatio,
                    '--ic-radius': this.borderRadius,
                };
            },

            trackStyle() {
                return {
                    transform: `translate3d(${-(this.position - this.centerOffset)}px, 0, 0)`,
                    transition: this.useTransition
                        ? `transform ${this.transitionMs}ms ease`
                        : 'none',
                };
            },
        },

        mounted() {
            this.recalculate();

            // Diferimos el recálculo a la siguiente frame: recalculate() modifica el
            // layout interno y, si se ejecuta dentro del propio callback del observer,
            // el navegador avisa con "ResizeObserver loop completed with undelivered
            // notifications". rAF rompe ese bucle síncrono.
            this._onResize = () => {
                if (this._rafId) return;
                this._rafId = requestAnimationFrame(() => {
                    this._rafId = null;
                    this.recalculate();
                });
            };

            // ResizeObserver evita listeners caros y recalcula solo cuando cambia el ancho
            if (typeof ResizeObserver !== 'undefined') {
                this._observer = new ResizeObserver(this._onResize);
                this._observer.observe(this.$refs.root);
            } else {
                window.addEventListener('resize', this._onResize);
            }
        },

        beforeUnmount() {
            if (this._rafId) {
                cancelAnimationFrame(this._rafId);
                this._rafId = null;
            }
            if (this._observer) {
                this._observer.disconnect();
            } else {
                window.removeEventListener('resize', this._onResize);
            }
        },

        methods: {
            recalculate() {
                const root = this.$refs.root;
                if (!root) return;

                const containerWidth = root.clientWidth;
                if (!containerWidth || !this.images.length) return;

                const isMobile = window.innerWidth < this.breakpoint;
                const items = isMobile ? this.itemsMobile : this.itemsDesktop;

                // Ancho de cada foto descontando las separaciones visibles
                this.itemWidth = (containerWidth - this.gap * (items - 1)) / items;

                // Centramos la banda de fotos enteras visibles (tanto en móvil como en
                // escritorio): el espacio fraccionario sobrante se reparte por igual a
                // ambos lados, dejando ver una porción de la foto anterior y la siguiente.
                // Ej.: con items = 4.5 se ven 0.25 + 4 enteras + 0.25.
                const whole = Math.floor(items);
                const wholeBand = whole * this.itemWidth + this.gap * (whole - 1);
                this.centerOffset = (containerWidth - wholeBand) / 2;

                // Copias suficientes para cubrir lo visible más un set de margen a cada
                // lado (el centrado necesita contenido también a la izquierda)
                const needed = (containerWidth + 2 * this.oneSetWidth) / this.oneSetWidth;
                this.repeats = Math.max(3, Math.ceil(needed) + 1);

                this.useTransition = false;
                this.normalize();
            },

            // Mantiene la posición dentro de [oneSetWidth, 2·oneSetWidth): como el contenido
            // se repite, el reajuste es visualmente idéntico y por tanto invisible. Dejamos
            // un set completo de margen a la izquierda para que el centrado (centerOffset)
            // siempre tenga foto que mostrar en la porción anterior.
            normalize() {
                const set = this.oneSetWidth;
                if (!set) return;
                this.position = set + ((((this.position - set) % set) + set) % set);
            },

            // Lee el desplazamiento real (útil si hay una transición en curso)
            currentTranslate() {
                const track = this.$refs.track;
                if (!track) return this.position;
                const transform = getComputedStyle(track).transform;
                if (!transform || transform === 'none') return this.position;
                const matrix = new DOMMatrixReadOnly(transform);
                // El translate visual descuenta centerOffset; lo sumamos para recuperar
                // la posición lógica (borde izquierdo de la foto activa)
                return -matrix.m41 + this.centerOffset;
            },

            next() {
                this.useTransition = true;
                this.position += this.step * this.itemStep;
            },

            prev() {
                this.useTransition = true;
                this.position -= this.step * this.itemStep;
            },

            onPointerDown(e) {
                // Tomamos la posición visual actual por si veníamos de una animación
                this.position = this.currentTranslate();
                this.normalize();
                this.useTransition = false;
                this.dragging = true;
                this.lastPointerX = e.clientX;
                this.$refs.viewport.setPointerCapture(e.pointerId);
            },

            onPointerMove(e) {
                if (!this.dragging) return;
                e.preventDefault();
                const dx = e.clientX - this.lastPointerX;
                this.lastPointerX = e.clientX;
                this.position -= dx;
                this.normalize();
            },

            onPointerUp(e) {
                if (!this.dragging) return;
                this.dragging = false;
                if (this.$refs.viewport.hasPointerCapture(e.pointerId)) {
                    this.$refs.viewport.releasePointerCapture(e.pointerId);
                }
                if (this.snap) {
                    this.useTransition = true;
                    this.position = Math.round(this.position / this.itemStep) * this.itemStep;
                }
            },

            // Al terminar la animación reajustamos al rango sin transición (salto invisible)
            onTransitionEnd() {
                this.useTransition = false;
                this.normalize();
            },
        },
    };
</script>

<style scoped>
    .ic-carousel {
        position: relative;
        width: 100%;
    }

    .ic-viewport {
        width: 100%;
        overflow: hidden;
        cursor: grab;
        /* Permite el scroll vertical de la página y deja el gesto horizontal al carrusel */
        touch-action: pan-y;
        /* Aísla los repintados de la animación del resto de la página */
        contain: content;
    }

    .ic-viewport.ic-dragging {
        cursor: grabbing;
    }

    .ic-track {
        display: flex;
        gap: var(--ic-gap);
        width: max-content;
        will-change: transform;
        user-select: none;
    }

    .ic-item {
        flex: 0 0 var(--ic-item-width);
        width: var(--ic-item-width);
    }

    .ic-img {
        display: block;
        width: 100%;
        aspect-ratio: var(--ic-aspect);
        object-fit: cover;
        border-radius: var(--ic-radius);
        background-color: #e9eef2;
        pointer-events: none;
    }

    .ic-arrow {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 44px;
        height: 44px;
        border: 0;
        border-radius: 50%;
        background-color: rgba(255, 255, 255, 0.9);
        color: #0d47a1;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2;
        transition: background-color 0.2s ease;
    }

    .ic-arrow:hover {
        background-color: #fff;
    }

    .ic-arrow--prev {
        left: 8px;
    }

    .ic-arrow--next {
        right: 8px;
    }

    .ic-arrow__icon {
        font-size: 28px;
        line-height: 1;
        margin-top: -3px;
    }
</style>
